/**
 * Platform Manager
 * 
 * Unified interface for managing platform-specific SDKs.
 * Automatically detects the current platform and initializes the appropriate SDK.
 */

import { CrazyGamesSDK } from './CrazyGamesSDK';
import { PokiSDK } from './PokiSDK';
import { GameMonetizeSDK } from './GameMonetizeSDK';
import { PLATFORMS } from '../config/Constants';

export type PlatformType = typeof PLATFORMS[keyof typeof PLATFORMS];

/**
 * Platform Manager Singleton
 * 
 * Provides a unified API for platform-specific functionality.
 * Automatically detects and initializes the correct SDK.
 */
export class PlatformManager {
  private static instance: PlatformManager;
  private platform: PlatformType = PLATFORMS.WEB;
  private crazyGames: CrazyGamesSDK;
  private poki: PokiSDK;
  private gameMonetize: GameMonetizeSDK;
  private isInitialized = false;
  
  private constructor() {
    this.crazyGames = CrazyGamesSDK.getInstance();
    this.poki = PokiSDK.getInstance();
    this.gameMonetize = GameMonetizeSDK.getInstance();
  }
  
  /**
   * Get singleton instance
   */
  static getInstance(): PlatformManager {
    if (!PlatformManager.instance) {
      PlatformManager.instance = new PlatformManager();
    }
    return PlatformManager.instance;
  }
  
  /**
   * Initialize platform detection and SDK
   */
  async init(): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    
    this.isInitialized = true;
    
    // Detect platform from URL
    this.platform = this.detectPlatform();
    console.log(`[PlatformManager] Detected platform: ${this.platform}`);
    
    // Initialize appropriate SDK
    switch (this.platform) {
      case PLATFORMS.CRAZYGAMES:
        await this.crazyGames.init();
        break;
      case PLATFORMS.POKI:
        await this.poki.init();
        break;
      case PLATFORMS.GAMEMONETIZE:
        await this.gameMonetize.init();
        break;
      default:
        // Web platform, initialize all for testing
        await this.crazyGames.init();
        await this.poki.init();
        await this.gameMonetize.init();
        break;
    }
    
    console.log('[PlatformManager] Initialization complete');
  }
  
  /**
   * Detect current platform from URL
   */
  private detectPlatform(): PlatformType {
    const hostname = window.location.hostname.toLowerCase();
    
    if (hostname.includes('crazygames')) {
      return PLATFORMS.CRAZYGAMES;
    }
    
    if (hostname.includes('poki')) {
      return PLATFORMS.POKI;
    }
    
    if (hostname.includes('gamemonetize') || hostname.includes('gamedistribution')) {
      return PLATFORMS.GAMEMONETIZE;
    }
    
    // Check URL parameters for testing
    const urlParams = new URLSearchParams(window.location.search);
    const platformParam = urlParams.get('platform');
    
    if (platformParam === 'crazygames') {
      return PLATFORMS.CRAZYGAMES;
    }
    
    if (platformParam === 'poki') {
      return PLATFORMS.POKI;
    }
    
    if (platformParam === 'gamemonetize') {
      return PLATFORMS.GAMEMONETIZE;
    }
    
    return PLATFORMS.WEB;
  }
  
  /**
   * Get current platform
   */
  getPlatform(): PlatformType {
    return this.platform;
  }
  
  /**
   * Check if running on CrazyGames
   */
  isCrazyGames(): boolean {
    return this.platform === PLATFORMS.CRAZYGAMES;
  }
  
  /**
   * Check if running on Poki
   */
  isPoki(): boolean {
    return this.platform === PLATFORMS.POKI;
  }
  
  /**
   * Check if running on GameMonetize
   */
  isGameMonetize(): boolean {
    return this.platform === PLATFORMS.GAMEMONETIZE;
  }
  
  /**
   * Notify platforms that game loading has started
   */
  gameLoadingStart(): void {
    this.crazyGames.loadingStart();
    this.poki.gameLoadingStart();
  }
  
  /**
   * Notify platforms that game loading has finished
   */
  gameLoadingFinished(): void {
    this.crazyGames.loadingStop();
    this.poki.gameLoadingFinished();
  }
  
  /**
   * Notify platforms that gameplay has started
   */
  gameplayStart(): void {
    this.crazyGames.gameplayStart();
    this.poki.gameplayStart();
    this.gameMonetize.gameplayStart();
  }
  
  /**
   * Notify platforms that gameplay has stopped
   */
  gameplayStop(): void {
    this.crazyGames.gameplayStop();
    this.poki.gameplayStop();
    this.gameMonetize.gameplayStop();
  }
  
  /**
   * Trigger happy time (CrazyGames only)
   */
  happytime(): void {
    this.crazyGames.happytime();
  }
  
  /**
   * Request an interstitial/midgame ad
   * @param onComplete - Callback when ad finishes
   */
  async requestInterstitialAd(onComplete?: () => void): Promise<void> {
    if (this.platform === PLATFORMS.CRAZYGAMES) {
      this.crazyGames.requestAd('midgame', () => {
        onComplete?.();
      });
    } else if (this.platform === PLATFORMS.POKI) {
      await this.poki.commercialBreak();
      onComplete?.();
    } else if (this.platform === PLATFORMS.GAMEMONETIZE) {
      await this.gameMonetize.requestAd();
      onComplete?.();
    } else {
      // Simulate ad on web
      await new Promise(resolve => setTimeout(resolve, 300));
      onComplete?.();
    }
  }
  
  /**
   * Request a rewarded video ad
   * @returns Promise resolving to true if reward should be given
   */
  async requestRewardedAd(): Promise<boolean> {
    if (this.platform === PLATFORMS.CRAZYGAMES) {
      return new Promise((resolve) => {
        this.crazyGames.requestAd('rewarded', (success, rewarded) => {
          resolve(success && rewarded === true);
        });
      });
    } else if (this.platform === PLATFORMS.POKI) {
      return await this.poki.rewardedBreak();
    } else if (this.platform === PLATFORMS.GAMEMONETIZE) {
      // GameMonetize doesn't have separate rewarded ads, use regular ad
      return await this.gameMonetize.requestAd();
    } else {
      // Simulate rewarded ad on web (always successful)
      await new Promise(resolve => setTimeout(resolve, 300));
      return true;
    }
  }
  
  // ===== DATA MODULE METHODS =====
  
  /**
   * Check if Data Module is available (CrazyGames only)
   */
  isDataModuleAvailable(): boolean {
    return this.platform === PLATFORMS.CRAZYGAMES && this.crazyGames.isDataModuleAvailable();
  }
  
  /**
   * Get item from platform storage
   * Uses CrazyGames Data Module if available, otherwise localStorage
   * @param key - Storage key
   * @returns The stored value or null
   */
  async getItem(key: string): Promise<string | null> {
    if (this.platform === PLATFORMS.CRAZYGAMES) {
      return await this.crazyGames.getItem(key);
    }
    return localStorage.getItem(key);
  }
  
  /**
   * Set item in platform storage
   * Uses CrazyGames Data Module if available, otherwise localStorage
   * @param key - Storage key
   * @param value - Value to store
   */
  async setItem(key: string, value: string): Promise<void> {
    if (this.platform === PLATFORMS.CRAZYGAMES) {
      await this.crazyGames.setItem(key, value);
      return;
    }
    localStorage.setItem(key, value);
  }
  
  /**
   * Remove item from platform storage
   * Uses CrazyGames Data Module if available, otherwise localStorage
   * @param key - Storage key
   */
  async removeItem(key: string): Promise<void> {
    if (this.platform === PLATFORMS.CRAZYGAMES) {
      await this.crazyGames.removeItem(key);
      return;
    }
    localStorage.removeItem(key);
  }
}

export default PlatformManager;
